import Box from '@mui/material/Box';
import { alpha, useTheme } from '@mui/material/styles';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import React, { useCallback } from 'react';

import { QuickToggleGroupProps } from './QuickToggleGroup.props';
import { STANDARD_FIELD_HEIGHT } from '../../../configs/ui-constants';

/**
 * A compact segmented control for switching a list between mutually exclusive
 * views — one click to change, rather than opening a dropdown and ticking.
 *
 * Selection is exclusive and always populated: clicking the active option is a
 * no-op rather than deselecting it, so the list can never end up filtered by
 * nothing-in-particular. Give the group an explicit "All" option instead.
 *
 * Styling follows the V4 toggle convention established by RecentJobsPanel:
 * outlined in `v4.border`, selected fills with `v4.accent`.
 */
export const QuickToggleGroup: React.FC<QuickToggleGroupProps> = ({
  label,
  options,
  value,
  onChange,
}) => {
  const theme = useTheme();

  const handleChange = useCallback(
    (_event: React.MouseEvent<HTMLElement>, next: string | null) => {
      // MUI reports null when the active button is clicked again. Ignoring it is
      // what keeps a selection always present.
      if (next !== null) onChange(next);
    },
    [onChange]
  );

  return (
    <ToggleButtonGroup
      value={value}
      exclusive
      onChange={handleChange}
      size="small"
      aria-label={label}
      sx={{ flexWrap: 'wrap', gap: 0.5, border: 'none' }}
    >
      {options.map((option) => (
        <ToggleButton
          key={option.value}
          value={option.value}
          aria-label={option.label}
          sx={{
            textTransform: 'none',
            fontSize: '0.75rem',
            fontWeight: 500,
            px: 1.25,
            py: 0,
            gap: 0.5,
            height: STANDARD_FIELD_HEIGHT,
            minHeight: STANDARD_FIELD_HEIGHT,
            boxSizing: 'border-box',
            whiteSpace: 'nowrap',
            color: theme.palette.text.primary,
            border: `1px solid ${theme.palette.v4.border}`,
            // Every button keeps its own border and radius, so the group reads as
            // separate pills rather than MUI's default fused bar.
            borderRadius: 1,
            '&:not(:first-of-type)': {
              borderLeft: `1px solid ${theme.palette.v4.border}`,
              borderRadius: 1,
            },
            '&:not(:last-of-type)': { borderRadius: 1 },
            '&:hover': { bgcolor: alpha(theme.palette.v4.accent, 0.08) },
            '&.Mui-selected': {
              bgcolor: theme.palette.v4.accent,
              color: theme.palette.v4.surface,
              borderColor: theme.palette.v4.accent,
              '&:hover': { bgcolor: theme.palette.v4.accent },
            },
          }}
        >
          {option.label}
          {option.count !== undefined && (
            <Box
              component="span"
              // Dimmed rather than a separate colour, so it reads as secondary in
              // both the selected (on accent) and unselected (on surface) states.
              sx={{ opacity: 0.7, fontVariantNumeric: 'tabular-nums' }}
            >
              {option.count.toLocaleString()}
            </Box>
          )}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
};
